import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { auditService } from '../../services/auditService';
import { AuditLogEntry } from '../../types/audit';
import UnifiedAuditDiff from './UnifiedAuditDiff';
import { useStatusLabel } from './auditEvents/AuditEventRow';

interface AuditHistoryTabProps {
  resourceType: string;
  resourceId: string;
}

const PAGE_SIZE = 50;

const topChangedKeys = (oldVal: unknown, newVal: unknown, limit = 3): string[] => {
  const toDict = (v: unknown): Record<string, unknown> =>
    v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
  const a = toDict(oldVal);
  const b = toDict(newVal);
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  const changed: string[] = [];
  for (const k of keys) {
    if (JSON.stringify(a[k]) !== JSON.stringify(b[k])) {
      changed.push(k);
      if (changed.length >= limit) break;
    }
  }
  return changed;
};

/**
 * The history of one resource inside its detail modal: one entry per
 * recorded change, newest first; an entry opens to show the diff.
 */
const AuditHistoryTab: React.FC<AuditHistoryTabProps> = ({ resourceType, resourceId }) => {
  const { t, formatDateTime, formatNumber, formatRelativeTime } = useI18n();
  const [items, setItems] = useState<AuditLogEntry[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [showReads, setShowReads] = useState(false);

  const load = useCallback(
    async (targetPage: number, append: boolean) => {
      setLoading(true);
      setError(null);
      try {
        const response = await auditService.listAuditLog({
          resource_type: resourceType,
          resource_id: resourceId,
          exclude_reads: !showReads,
          page: targetPage,
          page_size: PAGE_SIZE,
        });
        setItems((prev) => (append ? [...prev, ...(response.items || [])] : response.items || []));
        setTotal(response.total || 0);
      } catch (err) {
        console.error('[AuditHistoryTab] load failed:', err);
        setError(err instanceof Error ? err.message : t('audit.history.errorLoading'));
      } finally {
        setLoading(false);
      }
    },
    [resourceType, resourceId, showReads, t],
  );

  useEffect(() => {
    setPage(1);
    setExpanded(null);
    load(1, false);
    // `load` changes with the same dependencies; listing it would double-load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceType, resourceId, showReads]);

  const handleLoadMore = useCallback(() => {
    const next = page + 1;
    setPage(next);
    load(next, true);
  }, [page, load]);

  const hasMore = useMemo(() => items.length < total, [items, total]);

  const statusLabel = useStatusLabel();

  return (
    <div className="tab-content aud-history" data-testid="audit-history-tab">
      <section className="cu-section">
        <header className="cu-section-head">
          <h3>{t('audit.history.tabLabel')}<span className="ud-tab-count">{formatNumber(total)}</span></h3>
          <p>{t('audit.history.intro')}</p>
          <div className="cu-section-actions">
            <label className="tpl-check">
              <input
                type="checkbox"
                checked={showReads}
                onChange={(e) => setShowReads(e.target.checked)}
                data-testid="audit-history-show-reads"
              />
              {showReads ? t('audit.history.hideReads') : t('audit.history.showReads')}
            </label>
          </div>
        </header>

        {error && <div className="error-message" role="alert">{error}</div>}

        {loading && items.length === 0 ? (
          <div className="tpd-empty"><p>{t('audit.history.loading')}</p></div>
        ) : items.length === 0 ? (
          <div className="tpd-empty"><p>{t('audit.history.empty')}</p></div>
        ) : (
          <div className="aud-history-list">
            {items.map((entry) => {
              const isExpanded = expanded === entry.id;
              const changedKeys = topChangedKeys(entry.old_value, entry.new_value);
              return (
                <div
                  key={entry.id}
                  className={`aud-history-entry${isExpanded ? ' is-expanded' : ''}`}
                  data-testid={`audit-history-entry-${entry.id}`}
                >
                  <button
                    type="button"
                    className="aud-history-head"
                    onClick={() => setExpanded(isExpanded ? null : entry.id)}
                    aria-expanded={isExpanded}
                  >
                    <span className={`aud-status is-${entry.status}`}>{statusLabel(entry.status)}</span>
                    <span className="aud-history-action">{entry.action}</span>
                    <span className="aud-history-actor">{entry.actor_username || t('audit.table.anonymous')}</span>
                    <span className="aud-history-when" title={formatDateTime(entry.timestamp)}>{formatRelativeTime(entry.timestamp)}</span>
                  </button>
                  {changedKeys.length > 0 && !isExpanded && (
                    <div className="aud-history-summary">
                      {changedKeys.join(', ')}{changedKeys.length >= 3 ? '…' : ''}
                    </div>
                  )}
                  {isExpanded && (
                    <div className="aud-history-detail">
                      <UnifiedAuditDiff oldValue={entry.old_value} newValue={entry.new_value} />
                      {entry.error_message && (
                        <div className="aud-error-box" role="alert">
                          <strong>{t('audit.row.errorMessage')}</strong> {entry.error_message}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {hasMore && (
          <button
            type="button"
            className="btn btn-secondary btn-sm aud-history-more"
            onClick={handleLoadMore}
            disabled={loading}
            data-testid="audit-history-load-more"
          >
            {t('audit.history.loadMore')}
          </button>
        )}
      </section>
    </div>
  );
};

export default AuditHistoryTab;
